import { useState } from 'react'
import type { JSX } from 'react'
import { ANALYSIS_DEFAULTS, SEGMENTATION_DEFAULTS } from '../../api/client'
import { ApiError, describeError } from '../../api/errors'
import type { AnalyzerStatus } from '../../api/serverStatus'
import type { AnalysisOptions, SegmentationMode, SegmentationOptions } from '../../api/types'
import { formatTime } from '../../lib/format'
import { Modal } from '../Modal'

export type AnalyzeDialogProps = {
  analyzer: AnalyzerStatus
  videoDuration: number
  existingLines: number
  /** Бросает — диалог остаётся открытым и показывает ошибку. */
  onRun: (options: AnalysisOptions) => Promise<void>
  onClose: () => void
}

type SpeakersMode = 'auto' | 'exact' | 'range'

const LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'ru', label: 'русский' },
  { code: 'en', label: 'английский' },
]

const SEGMENTATION_MODES: ReadonlyArray<{ value: SegmentationMode; label: string; hint: string }> = [
  {
    value: 'vad+energy',
    label: 'VAD и энергия (по умолчанию)',
    hint: 'Границы задаёт детектор голоса плюс энергетический — так в разметку попадают крики и дыхание.',
  },
  {
    value: 'vad',
    label: 'Только VAD',
    hint: 'Детектор речи без энергетического: тише ложных срабатываний, но вокализации теряются.',
  },
  {
    value: 'asr',
    label: 'По распознаванию (прежнее поведение)',
    hint: 'Границы берутся у Whisper. На чистой студийной речи аккуратнее, но всё без слов пропадает.',
  },
]

const SEGMENTATION_KEYS = Object.keys(SEGMENTATION_DEFAULTS) as Array<keyof SegmentationOptions>

/** Блок не трогали — не шлём его вовсе (docs/SEGMENTATION.md, «Параметры»). */
function isDefaultSegmentation(value: SegmentationOptions): boolean {
  return SEGMENTATION_KEYS.every((key) => value[key] === SEGMENTATION_DEFAULTS[key])
}

/**
 * Параметры сегментации появились позже эндпоинта разбора, и бэкенд может их
 * ещё не знать. 400 в этом случае — не поломка интерфейса, а разошедшиеся
 * версии, и сказать об этом надо прямо.
 */
function explainFailure(err: unknown, sentSegmentation: boolean): string {
  if (sentSegmentation && err instanceof ApiError && err.status === 400) {
    return `Сервер отклонил параметры сегментации — похоже, бэкенд про них ещё не знает. Верните значения по умолчанию (кнопка в блоке «Дополнительно»), и разбор запустится по-старому. Ответ сервера: ${describeError(err)}`
  }
  return describeError(err)
}

/**
 * Разбор идёт не быстрее реального времени даже на GPU, а на CPU кратно
 * дольше. Точную цифру никто не знает, поэтому даём порядок величины,
 * а не обещание.
 */
function estimate(duration: number, device: string): string {
  if (duration <= 0) return 'Времени уйдёт заметно больше, чем длится видео.'
  const cpu = device === 'cpu'
  const low = duration * (cpu ? 5 : 1)
  const high = duration * (cpu ? 20 : 3)
  return `Видео ${formatTime(duration)} — ориентировочно ${formatTime(low)}…${formatTime(high)} работы${
    cpu ? ' на процессоре' : ''
  }.`
}

export function AnalyzeDialog({
  analyzer,
  videoDuration,
  existingLines,
  onRun,
  onClose,
}: AnalyzeDialogProps): JSX.Element {
  const [speakersMode, setSpeakersMode] = useState<SpeakersMode>('auto')
  const [speakers, setSpeakers] = useState(2)
  const [minSpeakers, setMinSpeakers] = useState(2)
  const [maxSpeakers, setMaxSpeakers] = useState(4)
  const [translate, setTranslate] = useState(ANALYSIS_DEFAULTS.translate)
  const [targetLanguage, setTargetLanguage] = useState(ANALYSIS_DEFAULTS.targetLanguage)
  const [separate, setSeparate] = useState(ANALYSIS_DEFAULTS.separate)
  const [segmentation, setSegmentation] = useState<SegmentationOptions>(SEGMENTATION_DEFAULTS)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const rangeBroken = speakersMode === 'range' && maxSpeakers < minSpeakers
  const segmentationTouched = !isDefaultSegmentation(segmentation)

  const patchSegmentation = (patch: Partial<SegmentationOptions>): void => {
    setSegmentation((prev) => ({ ...prev, ...patch }))
  }

  const run = async (): Promise<void> => {
    if (rangeBroken) return
    setRunning(true)
    setError(null)
    const base: AnalysisOptions = {
      targetLanguage,
      speakers: speakersMode === 'exact' ? speakers : null,
      minSpeakers: speakersMode === 'range' ? minSpeakers : null,
      maxSpeakers: speakersMode === 'range' ? maxSpeakers : null,
      translate,
      separate,
    }
    try {
      await onRun(segmentationTouched ? { ...base, segmentation } : base)
    } catch (err) {
      setError(explainFailure(err, segmentationTouched))
    } finally {
      setRunning(false)
    }
  }

  return (
    <Modal
      title="Разобрать видео автоматически"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" onClick={onClose} disabled={running}>
            Отмена
          </button>
          <span className="spacer" />
          <button
            type="button"
            className="primary"
            disabled={running || rangeBroken}
            onClick={() => void run()}
          >
            {running ? 'Ставлю задачу…' : 'Запустить разбор'}
          </button>
        </>
      }
    >
      <div className="notice warn">
        <strong>Это черновик, а не готовый перевод</strong>
        <span className="faint">
          Разделение оставляет следы речи в музыке, диаризация путает похожие голоса,
          расшифровка врёт на именах, перевод машинный. Результат нужно будет проверить
          и поправить — применится он только по вашему подтверждению.
        </span>
      </div>

      <fieldset className="analysis-fieldset">
        <legend>Сколько человек говорит</legend>
        <p className="faint" style={{ margin: 0 }}>
          Точное число заметно повышает точность диаризации. Вы почти наверняка знаете
          ответ — машина нет.
        </p>
        <label className="row check">
          <input
            type="radio"
            name="analyze-speakers"
            checked={speakersMode === 'auto'}
            disabled={running}
            onChange={() => setSpeakersMode('auto')}
          />
          <span>Определить самому (менее точно)</span>
        </label>
        <label className="row check">
          <input
            type="radio"
            name="analyze-speakers"
            checked={speakersMode === 'exact'}
            disabled={running}
            onChange={() => setSpeakersMode('exact')}
          />
          <span>Ровно</span>
          <input
            type="number"
            className="tiny"
            min={1}
            max={20}
            step={1}
            value={speakers}
            disabled={running || speakersMode !== 'exact'}
            aria-label="Число говорящих"
            onChange={(e) => {
              const next = Number(e.currentTarget.value)
              if (Number.isFinite(next)) setSpeakers(Math.min(20, Math.max(1, Math.round(next))))
            }}
          />
        </label>
        <label className="row check">
          <input
            type="radio"
            name="analyze-speakers"
            checked={speakersMode === 'range'}
            disabled={running}
            onChange={() => setSpeakersMode('range')}
          />
          <span>От</span>
          <input
            type="number"
            className="tiny"
            min={1}
            max={20}
            step={1}
            value={minSpeakers}
            disabled={running || speakersMode !== 'range'}
            aria-label="Минимум говорящих"
            onChange={(e) => {
              const next = Number(e.currentTarget.value)
              if (Number.isFinite(next)) setMinSpeakers(Math.min(20, Math.max(1, Math.round(next))))
            }}
          />
          <span>до</span>
          <input
            type="number"
            className="tiny"
            min={1}
            max={20}
            step={1}
            value={maxSpeakers}
            disabled={running || speakersMode !== 'range'}
            aria-label="Максимум говорящих"
            onChange={(e) => {
              const next = Number(e.currentTarget.value)
              if (Number.isFinite(next)) setMaxSpeakers(Math.min(20, Math.max(1, Math.round(next))))
            }}
          />
        </label>
        {rangeBroken ? (
          <p className="notice warn" style={{ margin: 0 }}>
            Верхняя граница меньше нижней.
          </p>
        ) : null}
      </fieldset>

      <fieldset className="analysis-fieldset">
        <legend>Что делать со звуком и текстом</legend>
        <label className="row check">
          <input
            type="checkbox"
            checked={separate}
            disabled={running}
            onChange={(e) => setSeparate(e.currentTarget.checked)}
          />
          <span>Разделить на M&E и реплики</span>
        </label>
        <span className="faint">
          {separate
            ? 'Получится дорожка музыки и эффектов для режима полного дубляжа. Заодно расшифровка идёт по чистым репликам и ошибается реже.'
            : 'Без разделения дорожки M&E не будет, а расшифровка пойдёт по смешанному звуку — с музыкой Whisper ошибается заметно чаще.'}
        </span>

        <label className="row check">
          <input
            type="checkbox"
            checked={translate}
            disabled={running}
            onChange={(e) => setTranslate(e.currentTarget.checked)}
          />
          <span>Перевести реплики</span>
        </label>
        {translate ? (
          <div className="row">
            <label htmlFor="analyze-language" style={{ margin: 0 }}>
              Язык перевода
            </label>
            <select
              id="analyze-language"
              value={targetLanguage}
              disabled={running}
              style={{ width: 'auto' }}
              onChange={(e) => setTargetLanguage(e.currentTarget.value)}
            >
              {LANGUAGES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <span className="faint">
          {translate
            ? 'Перевод локальный и машинный: понятный подстрочник, а не художественный диалог.'
            : 'Оригинальный текст всё равно сохранится — переведёте вручную.'}
        </span>
      </fieldset>

      <details
        className="analysis-advanced"
        open={advancedOpen}
        onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}
      >
        <summary>
          Дополнительно: как искать реплики
          {segmentationTouched ? <span className="badge">изменено</span> : null}
        </summary>

        <div className="stack" style={{ marginTop: 8 }}>
          <p className="faint" style={{ margin: 0 }}>
            Границы реплик ищет детектор голоса по чистой дорожке, а не распознавание речи:
            иначе крик, стон и дыхание теряются целиком. Значения по умолчанию рассчитаны
            на обычное кино — трогать их нужно, только если результат не устроил.
          </p>

          <div>
            <label htmlFor="segmentation-mode">Чем задавать границы</label>
            <select
              id="segmentation-mode"
              value={segmentation.mode}
              disabled={running}
              onChange={(e) =>
                patchSegmentation({ mode: e.currentTarget.value as SegmentationMode })
              }
            >
              {SEGMENTATION_MODES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <span className="faint">
              {SEGMENTATION_MODES.find((item) => item.value === segmentation.mode)?.hint ?? ''}
            </span>
          </div>

          <label className="row check">
            <input
              type="checkbox"
              checked={segmentation.keepNonverbal}
              disabled={running || segmentation.mode === 'asr'}
              onChange={(e) => patchSegmentation({ keepNonverbal: e.currentTarget.checked })}
            />
            <span>Ловить звуки без слов</span>
          </label>
          <span className="faint">
            {segmentation.mode === 'asr'
              ? 'В режиме «по распознаванию» вокализаций не будет в любом случае: Whisper их не видит.'
              : segmentation.keepNonverbal
                ? 'Крики, стоны и дыхание попадут в разметку отдельными репликами с пометкой «без слов».'
                : 'Участки без распознанных слов будут выброшены — так же, как раньше.'}
          </span>

          <div>
            <label htmlFor="segmentation-vad">
              Чувствительность к голосу: <span className="mono">{segmentation.vadThreshold.toFixed(2)}</span>
            </label>
            <input
              id="segmentation-vad"
              type="range"
              min={0.1}
              max={0.9}
              step={0.05}
              value={segmentation.vadThreshold}
              disabled={running || segmentation.mode === 'asr'}
              onChange={(e) => patchSegmentation({ vadThreshold: Number(e.currentTarget.value) })}
            />
            <span className="faint">
              Меньше — ловит больше, включая шорохи; больше — только уверенную речь.
            </span>
          </div>

          <div>
            <label htmlFor="segmentation-energy">
              Порог громкости: <span className="mono">{segmentation.energyThresholdDb} дБ</span>
            </label>
            <input
              id="segmentation-energy"
              type="range"
              min={-70}
              max={-20}
              step={1}
              value={segmentation.energyThresholdDb}
              disabled={running || segmentation.mode !== 'vad+energy'}
              onChange={(e) =>
                patchSegmentation({ energyThresholdDb: Number(e.currentTarget.value) })
              }
            />
            <span className="faint">
              {segmentation.mode === 'vad+energy'
                ? 'Энергетический детектор берёт всё громче этого порога — им и ловится крик, который VAD речью не считает.'
                : 'Работает только в режиме «VAD и энергия».'}
            </span>
          </div>

          <div>
            <label htmlFor="segmentation-max">
              Максимальная длина реплики: <span className="mono">{segmentation.maxLineDuration} с</span>
            </label>
            <input
              id="segmentation-max"
              type="range"
              min={4}
              max={30}
              step={1}
              value={segmentation.maxLineDuration}
              disabled={running}
              onChange={(e) =>
                patchSegmentation({ maxLineDuration: Number(e.currentTarget.value) })
              }
            />
            <span className="faint">
              Длиннее — разрежется по самой большой внутренней паузе. Реплику на двенадцать
              секунд актёру не произнести за один дубль.
            </span>
          </div>

          {segmentationTouched ? (
            <div className="row wrap">
              <span className="faint">
                Параметры уйдут в запрос вместе с задачей. Если бэкенд их ещё не знает, разбор
                не запустится — тогда верните значения по умолчанию.
              </span>
              <span className="spacer" />
              <button
                type="button"
                className="ghost small"
                disabled={running}
                onClick={() => setSegmentation(SEGMENTATION_DEFAULTS)}
              >
                Вернуть по умолчанию
              </button>
            </div>
          ) : (
            <span className="faint">
              Ничего не менялось — блок параметров в запрос не попадёт, сервис возьмёт свои
              значения.
            </span>
          )}
        </div>
      </details>

      <div className="notice">
        <strong>Работа долгая</strong>
        <span className="faint">
          {estimate(videoDuration, analyzer.device)} Одновременно выполняется одна задача:
          модели не помещаются в память видеокарты дважды. Вкладку можно закрыть — прогресс
          подхватится при возвращении.
        </span>
      </div>

      {analyzer.device === 'cpu' ? (
        <p className="notice warn" style={{ margin: 0 }}>
          Сервис работает на процессоре — разбор будет в разы медленнее, чем на видеокарте.
        </p>
      ) : null}

      {existingLines > 0 ? (
        <p className="faint">
          В проекте уже {existingLines} реплик(и). Разбор их не тронет: заменять разметку или
          добавлять к ней, вы решите после проверки результата.
        </p>
      ) : null}

      {error ? <p className="notice warn">{error}</p> : null}
    </Modal>
  )
}
